<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Invoice from 'dashboard/api/invoice';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  order: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'success', 'openEnterpriseModal']);

const { t } = useI18n();

const isOpen = computed({
  get: () => props.show,
  set: value => {
    if (!value) emit('close');
  },
});

// 发票类型
const invoiceType = ref('enterprise');

// 企业信息列表
const enterpriseInfos = ref([]);
const selectedEnterpriseId = ref(null);

// 表单数据
const formData = ref({
  enterprise_name: '',
  tax_no: '',
  address: '',
  phone: '',
  bank_name: '',
  bank_account: '',
  email: '',
  remark: '',
});

const saveAsEnterprise = ref(false);
const isSubmitting = ref(false);
const submitError = ref('');

// 加载企业信息
const loadEnterpriseInfos = async () => {
  try {
    const response = await Invoice.getEnterpriseInfos();
    enterpriseInfos.value = response.data || [];

    // 如果有默认企业信息，自动选中
    const defaultInfo = enterpriseInfos.value.find(info => info.is_default);
    if (defaultInfo) {
      selectedEnterpriseId.value = defaultInfo.id;
    }
  } catch (error) {
    // Error handled silently - enterprise infos are optional
  }
};

// 选择已有企业信息
const selectEnterprise = enterpriseId => {
  if (!enterpriseId) {
    // 清空表单
    formData.value = {
      enterprise_name: '',
      tax_no: '',
      address: '',
      phone: '',
      bank_name: '',
      bank_account: '',
      email: '',
      remark: '',
    };
    return;
  }

  const enterprise = enterpriseInfos.value.find(
    info => info.id === enterpriseId
  );
  if (enterprise) {
    formData.value = {
      enterprise_name: enterprise.enterprise_name || '',
      tax_no: enterprise.tax_no || '',
      address: enterprise.address || '',
      phone: enterprise.phone || '',
      bank_name: enterprise.bank_name || '',
      bank_account: enterprise.bank_account || '',
      email: enterprise.email || '',
      remark: '',
    };
  }
};

// 监听选择变化
watch(selectedEnterpriseId, newId => {
  if (newId) {
    selectEnterprise(newId);
  }
});

// 表单验证
const validateForm = () => {
  if (!formData.value.enterprise_name.trim()) {
    submitError.value = t('BILLING_INVOICES.INVOICE.ENTERPRISE_NAME_REQUIRED');
    return false;
  }
  if (!formData.value.tax_no.trim()) {
    submitError.value = t('BILLING_INVOICES.INVOICE.TAX_NO_REQUIRED');
    return false;
  }
  if (!formData.value.email.trim()) {
    submitError.value = t('BILLING_INVOICES.INVOICE.EMAIL_REQUIRED');
    return false;
  }
  return true;
};

// 提交申请
const submitInvoice = async () => {
  submitError.value = '';

  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    // 如果勾选保存为企业信息
    if (saveAsEnterprise.value && invoiceType.value === 'enterprise') {
      await Invoice.createEnterpriseInfo({
        ...formData.value,
        is_default: false,
      });
    }

    // 申请发票
    await Invoice.applyInvoice(props.order.order_no, {
      invoice_type: invoiceType.value,
      enterprise_name: formData.value.enterprise_name,
      tax_no: formData.value.tax_no,
      email: formData.value.email,
      remark: formData.value.remark,
    });

    emit('success');
    isOpen.value = false;
  } catch (error) {
    submitError.value =
      error.response?.data?.error || t('BILLING_INVOICES.INVOICE.SUBMIT_ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

// 打开企业信息弹窗
const openEnterpriseModal = () => {
  emit('openEnterpriseModal');
};

onMounted(() => {
  if (isOpen.value) {
    loadEnterpriseInfos();
  }
});

watch(isOpen, newValue => {
  if (newValue) {
    loadEnterpriseInfos();
    // 重置表单
    formData.value = {
      enterprise_name: '',
      tax_no: '',
      address: '',
      phone: '',
      bank_name: '',
      bank_account: '',
      email: '',
      remark: '',
    };
    submitError.value = '';
  }
});
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
  >
    <div
      class="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      @click.stop
    >
      <!-- 头部 -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ $t('BILLING_INVOICES.INVOICE.MODAL_TITLE') }}
        </h2>
        <button
          class="p-2 rounded-lg hover:bg-n-slate-3 transition-colors"
          @click="isOpen = false"
        >
          <span class="i-lucide-x text-n-slate-11 text-xl" />
        </button>
      </div>

      <!-- 内容 -->
      <div class="p-6 space-y-6">
        <!-- 错误提示 -->
        <div v-if="submitError" class="p-4 text-red-600 bg-red-50 rounded-lg">
          {{ submitError }}
        </div>

        <!-- 发票类型 -->
        <div class="space-y-3">
          <label class="block text-sm font-medium text-n-slate-12">
            {{ $t('BILLING_INVOICES.INVOICE.INVOICE_TYPE') }}
          </label>
          <div class="flex gap-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="invoiceType"
                type="radio"
                value="personal"
                class="w-4 h-4 text-woot-500"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('BILLING_INVOICES.INVOICE.TYPE_PERSONAL') }}
              </span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="invoiceType"
                type="radio"
                value="enterprise"
                class="w-4 h-4 text-woot-500"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('BILLING_INVOICES.INVOICE.TYPE_ENTERPRISE') }}
              </span>
            </label>
          </div>
        </div>

        <!-- 企业信息 -->
        <div v-if="invoiceType === 'enterprise'" class="space-y-4">
          <div class="flex items-center justify-between">
            <label class="block text-sm font-medium text-n-slate-12">
              {{ $t('BILLING_INVOICES.INVOICE.ENTERPRISE_INFO') }}
            </label>
            <button
              v-if="enterpriseInfos.length > 0"
              type="button"
              class="text-sm text-woot-500 hover:text-woot-600"
              @click="openEnterpriseModal"
            >
              + {{ $t('BILLING_INVOICES.INVOICE.ADD_NEW') }}
            </button>
          </div>

          <!-- 选择已有企业信息 -->
          <div v-if="enterpriseInfos.length > 0" class="space-y-3">
            <label class="block text-sm text-n-slate-11">
              {{ $t('BILLING_INVOICES.INVOICE.SELECT_EXISTING') }}
            </label>
            <select
              v-model="selectedEnterpriseId"
              class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option :value="null">
                {{ $t('BILLING_INVOICES.INVOICE.ENTER_MANUAL') }}
              </option>
              <option
                v-for="info in enterpriseInfos"
                :key="info.id"
                :value="info.id"
              >
                {{ info.enterprise_name }} ({{ info.tax_no }})
              </option>
            </select>
          </div>

          <!-- 表单字段 -->
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ $t('BILLING_INVOICES.INVOICE.ENTERPRISE_NAME') }} *
                </label>
                <input
                  v-model="formData.enterprise_name"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                  :placeholder="
                    $t('BILLING_INVOICES.INVOICE.ENTERPRISE_NAME_PLACEHOLDER')
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ $t('BILLING_INVOICES.INVOICE.TAX_NO') }} *
                </label>
                <input
                  v-model="formData.tax_no"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                  :placeholder="
                    $t('BILLING_INVOICES.INVOICE.TAX_NO_PLACEHOLDER')
                  "
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('BILLING_INVOICES.INVOICE.ADDRESS') }}
              </label>
              <input
                v-model="formData.address"
                type="text"
                class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                :placeholder="
                  $t('BILLING_INVOICES.INVOICE.ADDRESS_PLACEHOLDER')
                "
              />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ $t('BILLING_INVOICES.INVOICE.PHONE') }}
                </label>
                <input
                  v-model="formData.phone"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                  :placeholder="
                    $t('BILLING_INVOICES.INVOICE.PHONE_PLACEHOLDER')
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ $t('BILLING_INVOICES.INVOICE.EMAIL') }} *
                </label>
                <input
                  v-model="formData.email"
                  type="email"
                  class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                  :placeholder="
                    $t('BILLING_INVOICES.INVOICE.EMAIL_PLACEHOLDER')
                  "
                />
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ $t('BILLING_INVOICES.INVOICE.BANK_NAME') }}
                </label>
                <input
                  v-model="formData.bank_name"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                  :placeholder="
                    $t('BILLING_INVOICES.INVOICE.BANK_NAME_PLACEHOLDER')
                  "
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  {{ $t('BILLING_INVOICES.INVOICE.BANK_ACCOUNT') }}
                </label>
                <input
                  v-model="formData.bank_account"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
                  :placeholder="
                    $t('BILLING_INVOICES.INVOICE.BANK_ACCOUNT_PLACEHOLDER')
                  "
                />
              </div>
            </div>

            <div
              v-if="selectedEnterpriseId === null"
              class="flex items-center gap-2"
            >
              <input
                id="saveAsEnterprise"
                v-model="saveAsEnterprise"
                type="checkbox"
                class="w-4 h-4 text-woot-500 rounded"
              />
              <label
                for="saveAsEnterprise"
                class="text-sm text-n-slate-12 cursor-pointer"
              >
                {{ $t('BILLING_INVOICES.INVOICE.SAVE_AS_ENTERPRISE') }}
              </label>
            </div>
          </div>
        </div>

        <!-- 备注 -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('BILLING_INVOICES.INVOICE.REMARK') }}
          </label>
          <textarea
            v-model="formData.remark"
            rows="3"
            class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 resize-none"
            :placeholder="$t('BILLING_INVOICES.INVOICE.REMARK_PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- 底部按钮 -->
      <div
        class="flex items-center justify-end gap-3 p-6 border-t border-n-weak bg-n-slate-1"
      >
        <Button
          variant="secondary"
          :label="$t('BILLING_INVOICES.INVOICE.CANCEL')"
          @click="isOpen = false"
        />
        <Button
          :label="$t('BILLING_INVOICES.INVOICE.SUBMIT')"
          :is-loading="isSubmitting"
          @click="submitInvoice"
        />
      </div>
    </div>
  </div>
</template>
